import {Injectable} from '@angular/core';
import {Principal} from '../principal.service';
import {UserRole} from '../user-role.model';

export enum RolePermission {
  PLACEMENT_CREATE = 'PLACEMENT_CREATE',
  PLACEMENT_EDIT = 'PLACEMENT_EDIT',
  PLACEMENT_ARCHIVE = 'PLACEMENT_ARCHIVE',
  AUDIO_PLACEMENT_CREATE = 'AUDIO_PLACEMENT_CREATE',
  AUDIO_PLACEMENT_EDIT = 'AUDIO_PLACEMENT_EDIT',
  AUDIO_PLACEMENT_ARCHIVE = 'AUDIO_PLACEMENT_ARCHIVE',
  DISPLAY_PLACEMENT_CREATE = 'DISPLAY_PLACEMENT_CREATE',
  DISPLAY_PLACEMENT_EDIT = 'DISPLAY_PLACEMENT_EDIT',
  DISPLAY_PLACEMENT_ARCHIVE = 'DISPLAY_PLACEMENT_ARCHIVE',
  APP_CSV_IMPORT = 'APP_CSV_IMPORT',
  APP_CREATE = 'APP_CREATE',
  APP_EDIT = 'APP_EDIT',
  APP_ARCHIVE = 'APP_ARCHIVE',
  APP_REQUEST_CREATE = 'APP_REQUEST_CREATE',
  APP_REQUEST_EDIT = 'APP_REQUEST_EDIT',
  APP_REQUEST_DISCONNECT = 'APP_REQUEST_DISCONNECT',
  APP_DP_IDS_CREATE = 'APP_DP_IDS_CREATE',
  TARGETING_CREATE = 'TARGETING_CREATE',
  TARGETING_READ = 'TARGETING_READ',
  TARGETING_EDIT = 'TARGETING_EDIT',
  TARGETING_ARCHIVE = 'TARGETING_ARCHIVE',
  ADSTXT_RECORDS_CREATE = 'ADSTXT_RECORDS_CREATE',
  ADSTXT_RECORDS_EDIT = 'ADSTXT_RECORDS_EDIT',
  ADSTXT_RECORDS_ARCHIVE = 'ADSTXT_RECORDS_ARCHIVE',
  ADSTXT_APPS_CRAWL = 'ADSTXT_APPS_CRAWL',
  ADSTXT_DOMAINS_CRAWL = 'ADSTXT_DOMAINS_CRAWL',
  MARKETPLACE_CREATE = 'MARKETPLACE_CREATE',
  MARKETPLACE_EDIT = 'MARKETPLACE_EDIT',
  PUBLISHER_CREATE = 'PUBLISHER_CREATE',
  PUBLISHER_EDIT = 'PUBLISHER_EDIT',
  PUBLISHER_ADSTXT_CRAWL = 'PUBLISHER_ADSTXT_CRAWL',
  PUBLISHER_A2V_CREATE = 'PUBLISHER_A2V_CREATE',
  PUBLISHER_APPROVALS_CREATE = 'PUBLISHER_APPROVALS_CREATE',
  DOMAIN_CREATE = 'DOMAIN_CREATE',
  DOMAIN_EDIT = 'DOMAIN_EDIT',
  DOMAIN_ARCHIVE = 'DOMAIN_ARCHIVE',
  NETWORK_CREATE = 'NETWORK_CREATE',
  NETWORK_EDIT = 'NETWORK_EDIT',
  NETWORK_ARCHIVE = 'NETWORK_ARCHIVE',
  DP_CREATE = 'DP_CREATE',
  DP_EDIT = 'DP_EDIT',
  DP_ARCHIVE = 'DP_ARCHIVE',
  DP_IMPORT_CSV = 'DP_IMPORT_CSV',
  CONNECTION_CREATE = 'CONNECTION_CREATE',
  CONNECTION_EDIT = 'CONNECTION_EDIT',
  CONNECTION_DUPLICATE = 'CONNECTION_DUPLICATE',
  COMPLIANCE_DOMAIN_CREATE = 'COMPLIANCE_DOMAIN_CREATE',
  COMPLIANCE_DOMAIN_EDIT = 'COMPLIANCE_DOMAIN_EDIT',
  SOURCES_CREATE = 'SOURCES_CREATE',
  SOURCES_EDIT = 'SOURCES_EDIT',
  SOURCES_ARCHIVE = 'SOURCES_ARCHIVE',
  SOURCES_TEMPLATE_CREATE = 'SOURCES_TEMPLATE_CREATE',
  SOURCES_TEMPLATE_EDIT = 'SOURCES_TEMPLATE_EDIT',
  SOURCES_TEMPLATE_ARCHIVE = 'SOURCES_TEMPLATE_ARCHIVE',
  IMAGE_CREATIVES_CREATE = 'IMAGE_CREATIVES_CREATE',
  IMAGE_CREATIVES_EDIT = 'IMAGE_CREATIVES_EDIT',
  IMAGE_CREATIVES_ARCHIVE = 'IMAGE_CREATIVES_ARCHIVE',
  ASSETS_CREATE = 'ASSETS_CREATE',
  ASSETS_EDIT = 'ASSETS_EDIT',
  GLOBAL_BRAND_TEMPLATE = 'GLOBAL_BRAND_TEMPLATE',
  BRAND_CREATE = 'BRAND_CREATE',
  BRAND_EDIT = 'BRAND_EDIT',
  NETWORK_TEMPLATE_CREATE = 'NETWORK_TEMPLATE_CREATE',
  COLLECTION_CREATE = 'COLLECTION_CREATE',
  COLLECTION_EDIT = 'COLLECTION_EDIT',
  VIDEOS_CREATE = 'VIDEOS_CREATE',
  VIDEOS_EDIT = 'VIDEOS_EDIT',
  VIDEOS_METADATA_CREATE = 'VIDEOS_METADATA_CREATE',
  CONTENT_OWNER_CREATE = 'CONTENT_OWNER_CREATE',
  CONTENT_OWNER_EDIT = 'CONTENT_OWNER_EDIT',
  USERS_CREATE = 'USERS_CREATE',
  USERS_EDIT = 'USERS_EDIT',
  RB_SCHEDULE_CREATE = 'RB_SCHEDULE_CREATE',
  CUSTOM_REPORTS_CREATE = 'CUSTOM_REPORTS_CREATE',
  ENDPOINT_GENERATOR_CREATE = 'ENDPOINT_GENERATOR_CREATE',
  OANDO_APPS_CREATE = 'OANDO_APPS_CREATE',
  OANDO_APPS_EDIT = 'OANDO_APPS_EDIT',
  CHANGELOG_EXPORT = 'CHANGELOG_EXPORT',
  CHANGELOG_READ = 'CHANGELOG_READ',
  SUPPLY_MARKETPLACE_CREATE = 'SUPPLY_MARKETPLACE_CREATE',
  SUPPLY_MARKETPLACE_EDIT = 'SUPPLY_MARKETPLACE_EDIT',
  SUPPLY_MARKETPLACE_ARCHIVE = 'SUPPLY_MARKETPLACE_ARCHIVE',
  FORECAST_CREATE = 'FORECAST_CREATE',
  ADVERTISER_CREATE = 'ADVERTISER_CREATE',
  ADVERTISER_EDIT = 'ADVERTISER_EDIT',
  ADVERTISER_ARCHIVE = 'ADVERTISER_ARCHIVE',
  PMP_CREATE = 'PMP_CREATE',
  PMP_EDIT = 'PMP_EDIT',
  PMP_ARCHIVE = 'PMP_ARCHIVE',
  TAXONOMY_GENRES_CREATE = 'TAXONOMY_GENRES_CREATE',
  TAXONOMY_GENRES_EDIT = 'TAXONOMY_GENRES_EDIT',
  TAXONOMY_GENRES_ARCHIVE = 'TAXONOMY_GENRES_ARCHIVE',
  MARKETPLACE_ROUTING_CREATE = 'MARKETPLACE_ROUTING_CREATE',
  MARKETPLACE_ROUTING_EDIT = 'MARKETPLACE_ROUTING_EDIT',
  MARKETPLACE_ROUTING_ARCHIVE = 'MARKETPLACE_ROUTING_ARCHIVE',

  // SRS-52/SRS-51: fail-closed like the backend's SYSTEM_LOG_SENSITIVE_VIEW permission - no role
  // row below grants it (SALES_REPRESENTATIVE's filter is allow-by-default, so it must be listed
  // as an explicit exclusion there - missed in the first pass, caught by CodeRabbit on MR !44),
  // so only ADMIN (which gets every RolePermission via RolesWithPermissions) is granted this via
  // the role table. Matches vb-core's RolePermissionMatrix: no role row carries it either, ADMIN
  // bypass + per-user direct grants only. A non-ADMIN backend direct grant isn't visible to this
  // static role table - LogsComponent additionally widens its reveal toggle at runtime whenever a
  // fetched row actually arrives unmasked, so a direct-grant user isn't locked out.
  SYSTEM_LOG_SENSITIVE_VIEW = 'SYSTEM_LOG_SENSITIVE_VIEW',

  // NSR-11: gates the entire debug-capture module (rules + captures), not just one control
  // within it - a rule dictates what sensitive traffic gets recorded, and captures may contain
  // PII, so even browsing rules is privileged. Matches vb-core's DebugCaptureRuleController 1:1
  // (permissionService.requirePermission(user, Permission.DEBUG_CAPTURE_MANAGE) on every route,
  // reads included) and its RBAC matrix row (docs/rbac-permission-matrix.md: no role listed,
  // ADMIN bypass + direct grants only) - fail-closed like SYSTEM_LOG_SENSITIVE_VIEW above, so no
  // role row below grants it either.
  DEBUG_CAPTURE_MANAGE = 'DEBUG_CAPTURE_MANAGE',

  // NSR-22: edit global / per-connection health thresholds. Viewing health detail is open to
  // anyone who can see connections; mutating thresholds is fail-closed (ADMIN / SUPER_ADMIN via
  // Object.values + direct grants only). Excluded from SALES_REPRESENTATIVE allow-by-default.
  CONNECTION_HEALTH_MANAGE = 'CONNECTION_HEALTH_MANAGE',

  // AIF-07 / AIF-05: read recommendations. Anomalies, forecasts, and actions are separate.
  // ADMIN / SUPER_ADMIN receive every value via Object.values. Action and settings
  // permissions are admin-only. Every AI_* name is excluded from the allow-by-default
  // role so READONLY_USER cannot inherit approve, reject, or settings.
  AI_INSIGHT_READ = 'AI_INSIGHT_READ',
  AI_ANOMALY_READ = 'AI_ANOMALY_READ',
  AI_FORECAST_READ = 'AI_FORECAST_READ',
  AI_RECOMMENDATION_ACTION = 'AI_RECOMMENDATION_ACTION',
  AI_ANOMALY_MANAGE = 'AI_ANOMALY_MANAGE',
  AI_SETTING_MANAGE = 'AI_SETTING_MANAGE',

  CAMPAIGNS_READ = 'campaigns:read',
  CAMPAIGNS_ORDERS_READ = 'campaigns.orders:read',
  CAMPAIGNS_ORDERS_ORDER_CREATE = 'campaigns.orders.order:create',
  CAMPAIGNS_ORDERS_ORDER_UPDATE = 'campaigns.orders.order:update',
  CAMPAIGNS_ORDERS_ORDER_ARCHIVE = 'campaigns.orders.order:archive',
  CAMPAIGNS_ORDERS_LINEITEM_READ = 'campaigns.orders.lineitem:read',
  CAMPAIGNS_ORDERS_LINEITEM_CREATE = 'campaigns.orders.lineitem:create',
  CAMPAIGNS_ORDERS_LINEITEM_UPDATE = 'campaigns.orders.lineitem:update',
  CAMPAIGNS_ORDERS_LINEITEM_DUPLICATE = 'campaigns.orders.lineitem:duplicate',
  CAMPAIGNS_ORDERS_LINEITEM_ARCHIVE = 'campaigns.orders.lineitem:archive',
  CAMPAIGNS_CREATIVES_READ = 'campaigns.creatives:read',
  CAMPAIGNS_CREATIVES_CREATE = 'campaigns.creatives:create',
  CAMPAIGNS_CREATIVES_UPDATE = 'campaigns.creatives:update',
  CAMPAIGNS_CREATIVES_ARCHIVE = 'campaigns.creatives:archive',
  CAMPAIGNS_CREATIVES_VAST = 'campaigns.creatives:vast',
  CAMPAIGNS_CREATIVES_MOMENTS_JSON = 'campaigns.creatives:moment_json',
  CAMPAIGNS_CREATIVES_SETTINGS = 'campaigns.creatives:settings',
  CAMPAIGNS_CREATIVES_PROCESS = 'campaigns.creatives:process',
}

const RolesWithPermissions: Record<string, RolePermission[]> = {

  [UserRole.ADMIN]: Object.values(RolePermission), // all
  // SUPER_ADMIN implies ADMIN backend-side (User.getAuthorities() synthesizes an extra ADMIN
  // GrantedAuthority whenever roles contains SUPER_ADMIN - see NSR-13 / vb-core User.java) -
  // mirror that implication here rather than re-deriving a separate permission set.
  [UserRole.SUPER_ADMIN]: Object.values(RolePermission), // all, same as ADMIN
  [UserRole.READ_ONLY_ADMIN]: Object.values(RolePermission).filter((ur: RolePermission) => {
    if (ur.includes('EDIT') || ur.includes('UPDATE')) {
      return ur;
    }
  }), //leave edits so user can open edit windows but cant save anything since all save buttons are locked behind create permissions
  // SALES_REPRESENTATIVE/CAMPAIGN_MANAGER/REV_OPS/DESIGNER below are no longer real backend roles
  // (2026-08-02 RBAC reconciliation folded them into USER/READONLY_USER - see
  // dxkulture-backend/docs/rbac-permission-matrix.md). Kept only because mask-select.component.ts
  // still sets them as principal.mask.role for the admin "preview as" shortcuts; no real user's
  // roles[] will contain these anymore, so these entries can't be reached via hasPermissions()'s
  // roles-array lookup for a real account. See USER/READONLY_USER below for what actually applies.
  [UserRole.SALES_REPRESENTATIVE]: Object.values(RolePermission).filter((ur: RolePermission) => ![
    RolePermission.PUBLISHER_CREATE,
    RolePermission.PUBLISHER_EDIT,
    RolePermission.PUBLISHER_ADSTXT_CRAWL,
    RolePermission.APP_ARCHIVE,
    RolePermission.ADSTXT_APPS_CRAWL,
    RolePermission.CHANGELOG_EXPORT,
    RolePermission.ENDPOINT_GENERATOR_CREATE,
    RolePermission.APP_EDIT,
    // SRS-52: this role's filter is allow-by-default (everything except this list), so
    // SYSTEM_LOG_SENSITIVE_VIEW must be excluded explicitly or it leaks in silently -
    // caught by CodeRabbit on MR !44. Matches the backend's fail-closed default (no role
    // row grants it there either, ADMIN bypass + direct grants only).
    RolePermission.SYSTEM_LOG_SENSITIVE_VIEW,
    // NSR-11: same allow-by-default leak risk - DEBUG_CAPTURE_MANAGE must be excluded
    // explicitly too, or this role would gain it by default like every other permission here.
    RolePermission.DEBUG_CAPTURE_MANAGE,
    // NSR-22: threshold edits are admin-only; exclude from allow-by-default.
    RolePermission.CONNECTION_HEALTH_MANAGE,
    RolePermission.AI_INSIGHT_READ,
    RolePermission.AI_ANOMALY_READ,
    RolePermission.AI_FORECAST_READ,
    RolePermission.AI_RECOMMENDATION_ACTION,
    RolePermission.AI_ANOMALY_MANAGE,
    RolePermission.AI_SETTING_MANAGE].includes(ur)),
  [UserRole.CAMPAIGN_MANAGER]: Object.values(RolePermission).filter((ur: RolePermission) => {
    if (!ur.startsWith('AI_') && (ur.includes('campaigns') || ur.includes('ADVERTISER') || ur.includes('SUPPLY_MARKETPLACE') || ur.includes('FORECAST') || ur.includes('PMP') || ur.includes('TARGETING'))) {
      return ur;
    }
  }),
  [UserRole.REV_OPS]: Object.values(RolePermission).filter((ur: RolePermission) => {
    if (ur.includes('forecast') || ur.includes('SUPPLY_MARKETPLACE')) {
      return ur;
    }
  }),
  [UserRole.DESIGNER]: [RolePermission.CAMPAIGNS_CREATIVES_CREATE, RolePermission.CAMPAIGNS_CREATIVES_READ, RolePermission.CAMPAIGNS_CREATIVES_SETTINGS, RolePermission.CAMPAIGNS_CREATIVES_UPDATE, RolePermission.CAMPAIGNS_CREATIVES_PROCESS, RolePermission.TARGETING_READ, RolePermission.TARGETING_CREATE, RolePermission.TARGETING_EDIT],
  // SRS-47: ADVERTISER / FINANCE are read-only for campaign entities; reports use POST /api/report (no RBAC perm gate).
  [UserRole.ADVERTISER]: [
    RolePermission.CAMPAIGNS_READ,
    RolePermission.CAMPAIGNS_ORDERS_READ,
    RolePermission.CAMPAIGNS_ORDERS_LINEITEM_READ,
    RolePermission.CAMPAIGNS_CREATIVES_READ,
    // Allows opening creative detail/settings dialogs; save actions remain CREATE-gated.
    RolePermission.CAMPAIGNS_CREATIVES_UPDATE,
    RolePermission.AI_INSIGHT_READ,
    RolePermission.AI_FORECAST_READ,
  ],
  [UserRole.FINANCE]: [
    RolePermission.CAMPAIGNS_READ,
    RolePermission.CAMPAIGNS_ORDERS_READ,
    RolePermission.CAMPAIGNS_ORDERS_LINEITEM_READ,
    RolePermission.AI_FORECAST_READ,
  ],
  [UserRole.PUBLISHER]: [
    RolePermission.RB_SCHEDULE_CREATE,
    RolePermission.AI_INSIGHT_READ,
    RolePermission.AI_FORECAST_READ,
  ]
};

// USER and READONLY_USER are what the backend actually issues today (see user-role.model.ts's
// enum comment and dxkulture-backend/docs/rbac-permission-matrix.md). Every real internal-staff
// account now holds one of these two, so without entries here hasPermissions() matched nothing
// for any of them - the bug this file was fixed for.
//
// USER folds CAMPAIGN_MANAGER + DESIGNER (+ the legacy REGULAR_USER/CONTENT_OWNER/
// OPERATIONS_REPRESENTATIVE roles, which never had dedicated FE entries). Backend's own
// reconciliation used a "reachability unchanged / union" rule for this fold (endpoint access
// unchanged from before - if any folded role could reach a route, the merged role still can), so
// this mirrors that: the union of what those two roles already granted here.
RolesWithPermissions[UserRole.USER] = Array.from(new Set([
  ...RolesWithPermissions[UserRole.CAMPAIGN_MANAGER],
  ...RolesWithPermissions[UserRole.DESIGNER],
]));
// READ_ONLY_ADMIN's filter above only keeps EDIT/UPDATE names, so the AI read permissions are added on their own.
RolesWithPermissions[UserRole.READ_ONLY_ADMIN] = [
  ...RolesWithPermissions[UserRole.READ_ONLY_ADMIN],
  RolePermission.AI_INSIGHT_READ,
  RolePermission.AI_ANOMALY_READ,
  RolePermission.AI_FORECAST_READ,
];

// READONLY_USER folds CLIENT_SERVICE_MANAGER (no FE entry ever existed for it) + SALES_REPRESENTATIVE
// + REV_OPS. Unlike USER above, the backend doc is explicit that READONLY_USER "has no
// *_CREATE/*_UPDATE/*_DELETE permission anywhere ... even where a folded-in legacy role used to
// have narrow write access" - so the union below is filtered down to non-mutating permissions,
// with RB_SCHEDULE_CREATE kept as an explicit exception (backend's matrix grants READONLY_USER
// REPORT_CONFIG_MANAGE/SCHEDULED_REPORT_MANAGE=Y - scheduling a report isn't a business mutation
// in that model).
// Case-insensitive: SALES_REPRESENTATIVE's allow-by-default filter includes the lowercase
// colon-separated CAMPAIGNS_* values too (e.g. 'campaigns.orders.order:create'), which a
// case-sensitive match would miss and leak into READONLY_USER (CodeRabbit MR !89).
const MUTATING_ACTION = /(CREATE|EDIT|ARCHIVE|DUPLICATE|IMPORT|UPLOAD|CRAWL|UPDATE|PROCESS|DISCONNECT)/i;
RolesWithPermissions[UserRole.READONLY_USER] = Array.from(new Set([
  ...RolesWithPermissions[UserRole.SALES_REPRESENTATIVE],
  ...RolesWithPermissions[UserRole.REV_OPS],
])).filter(p => p === RolePermission.RB_SCHEDULE_CREATE || !MUTATING_ACTION.test(p));

// Best-effort mechanical translation, not a verified line-by-line match against the backend
// matrix (the two taxonomies don't line up 1:1 - see the matrix's per-entity permissions vs. this
// file's per-feature ones). Flagged for QA/product sign-off, same as the backend doc's own
// "Behavior changes ... flagged explicitly for product/QA sign-off" section.

@Injectable()
export class RolePermissionService {

  constructor(private principal: Principal) {
  }

  hasPermissions(permissions: RolePermission[]) {
    const roles = this.principal.getUser()?.roles;
    if (permissions && permissions.length) {
      if (roles) {
        return roles.some(role => {
          const permissionsForRole = RolesWithPermissions[role] as RolePermission[];
          return permissionsForRole && permissionsForRole.some(p => permissions.includes(p));
        });
      }
    }
    return false;
  }
}

